/**
 * Icona e colore di ogni tipo di evento, anche sui pulsanti del Live Timer.
 *
 * Il colore dice l'esito per la squadra, non la categoria: verde cio che
 * guadagna, rosso cio che perde. I cartellini hanno il loro colore, che e il
 * significato stesso. I colori sono variabili del tema (`--evento-*` e
 * `--cartellino-*` in `src/index.css`), cosi seguono tema e modo scuro.
 */

import type { ComponentProps } from 'react';
import type { TipoEvento } from '@calcio/shared';
import { goalNet, soccerBall } from '@lucide/lab';
import {
  ArrowRightLeft,
  CircleX,
  createLucideIcon,
  Forward,
  Gift,
  Hand,
  HandGrab,
  HandHelping,
  LocateFixed,
  LocateOff,
  LogIn,
  LogOut,
  Play,
  RectangleVertical,
  Undo2,
  Whistle,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const SoccerBall = createLucideIcon('soccer-ball', soccerBall);
const GoalNet = createLucideIcon('goal-net', goalNet);

type Tono = 'buono' | 'cattivo' | 'neutro' | 'spento' | 'periodo' | 'giallo' | 'rosso';

const COLORE: Record<Tono, string> = {
  buono: 'text-evento-buono',
  cattivo: 'text-evento-cattivo',
  neutro: 'text-evento-neutro',
  spento: 'text-evento-spento',
  periodo: 'text-evento-periodo',
  giallo: 'text-cartellino-giallo',
  rosso: 'text-cartellino-rosso',
};

/** La sostituzione e una voce del tabellino, non un tipo di evento. */
type TipoIcona = TipoEvento | 'sostituzione';

const ICONA_EVENTO: Record<TipoIcona, { icona: LucideIcon; tono: Tono }> = {
  passaggio_completato: { icona: Forward, tono: 'buono' },
  passaggio_sbagliato: { icona: Undo2, tono: 'cattivo' },
  palla_conquistata: { icona: HandGrab, tono: 'buono' },
  palla_persa: { icona: Hand, tono: 'cattivo' },
  tiro_specchio: { icona: LocateFixed, tono: 'buono' },
  tiro_fuori: { icona: LocateOff, tono: 'cattivo' },
  gol: { icona: SoccerBall, tono: 'buono' },
  autogol: { icona: CircleX, tono: 'cattivo' },
  assist: { icona: HandHelping, tono: 'buono' },
  cartellino_giallo: { icona: RectangleVertical, tono: 'giallo' },
  cartellino_rosso: { icona: RectangleVertical, tono: 'rosso' },
  ingresso: { icona: LogIn, tono: 'neutro' },
  uscita: { icona: LogOut, tono: 'spento' },
  gol_subito: { icona: GoalNet, tono: 'cattivo' },
  autogol_avversario: { icona: Gift, tono: 'buono' },
  inizio_periodo: { icona: Play, tono: 'periodo' },
  fine_periodo: { icona: Whistle, tono: 'periodo' },
  sostituzione: { icona: ArrowRightLeft, tono: 'neutro' },
};

/**
 * Fondo pieno del colore dell'esito, per i pulsanti evento del Live Timer. Il
 * testo prende il colore del fondo pagina, chiaro in chiaro e scuro in scuro,
 * cosi resta leggibile su verde e rosso in entrambi i modi; il giallo vuole
 * sempre il nero. L'hover e ridichiarato perche su un telefono resta attivo
 * dopo il tocco, e il fondo del pulsante sparirebbe.
 */
const PIENO: Record<Tono, string> = {
  buono: 'bg-evento-buono text-background hover:bg-evento-buono/90 hover:text-background',
  cattivo: 'bg-evento-cattivo text-background hover:bg-evento-cattivo/90 hover:text-background',
  neutro: 'bg-evento-neutro text-background hover:bg-evento-neutro/90 hover:text-background',
  spento: 'bg-evento-spento text-background hover:bg-evento-spento/90 hover:text-background',
  periodo: 'bg-evento-periodo text-background hover:bg-evento-periodo/90 hover:text-background',
  giallo: 'bg-cartellino-giallo text-black hover:bg-cartellino-giallo/90 hover:text-black',
  rosso: 'bg-cartellino-rosso text-white hover:bg-cartellino-rosso/90 hover:text-white',
};

/** Pulsante evento del Live Timer: il colore dice l'esito prima dell'etichetta. */
export function PulsanteEvento({
  tipo,
  className,
  children,
  ...props
}: ComponentProps<typeof Button> & { tipo: TipoIcona }) {
  const { icona: Icona, tono } = ICONA_EVENTO[tipo];
  return (
    <Button className={cn(PIENO[tono], className)} {...props}>
      <Icona aria-hidden className="size-5" />
      {children}
    </Button>
  );
}

/** Decorativa: accanto c'e sempre l'etichetta dell'evento. */
export function IconaEvento({ tipo, className }: { tipo: TipoIcona; className?: string }) {
  const { icona: Icona, tono } = ICONA_EVENTO[tipo];
  return <Icona aria-hidden className={cn('inline size-4 shrink-0', COLORE[tono], className)} />;
}
